/**
 * AI 语音接待模块 - 直连 local-vox WebSocket
 *
 * 协议:
 *   握手:  {provider:"widget", params:{agent_id, from, to, inputSampleRate, modalities}}
 *   上行:  binary PCM16LE 16kHz mono frames (100ms = 3200 bytes)
 *   下行:  JSON transcription events + binary TTS PCM/WAV
 *
 * 与 public/widget.js 使用相同的 local-vox 协议。
 */
import { setPanelContent, getShadowRoot, setFabPulse, closePanel, showPanelBack } from "../../ui/container";
import { t } from "../../ui/i18n";
import { resolveApiBase } from "../../core/origin";
import { renderCallbackPanel } from "../callback/render";
import { phoneVerifyHtml, bindPhoneVerify, stopPvCountdown, type PhoneVerifyOptions } from "../phone-verify/render";
import type { Locale } from "../../types";

function apiBaseUrl(): string { return resolveApiBase(); }

/* ────── 状态 ────── */
type VoiceState = "idle" | "verifying" | "dialing" | "connected" | "ended" | "error";
type ViewMode = "waveform" | "chat";

let voiceState: VoiceState = "idle";
let viewMode: ViewMode = "waveform";
let manualHangup = false;
let storedWidgetId = "";
let codeSent = false;
let storedLocale: Locale = "zh-CN";
let voiceWs: WebSocket | null = null;
let audioCtx: AudioContext | null = null;
let micStream: MediaStream | null = null;
let scriptNode: ScriptProcessorNode | null = null;

// 动画状态: idle | thinking | responding | volume
let animMode: "idle" | "thinking" | "responding" | "volume" = "idle";
let isMuted = false;          // 通话中静音状态
let endRating: "up" | "down" | null = null;  // 结束页满意度

// 手机号 & 验证
let phoneNumber = "";
let phoneVerified = false;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let countdownSec = 0;

// 音频播放队列
let audioQueue: Float32Array[] = [];
let playing = false;
let nextPlayTime = 0;
let currentSource: AudioBufferSourceNode | null = null;  // 当前正在播放的音频源（用于打断时停止）

// PCM 发送缓冲
let pcmSendBuf: number[] = [];
const PCM_FRAME_BYTES = 3200;

// 通话计时器 & 聊天消息
let timerInterval: ReturnType<typeof setInterval> | null = null;
let callSec = 0;
let chatMessages: Array<{ role: "user" | "agent"; text: string }> = [];

// canvas 动画相关
let animFrameId: number | null = null;
let analyserNode: AnalyserNode | null = null;       // 麦克风（访客说话）电平
let playbackAnalyser: AnalyserNode | null = null;   // AI TTS 播放电平
let aiThinking = false;                              // WS 标记 AI 正在思考
let orbAmp = 0;                                      // 平滑后的振幅 0..1
let orbPhase = 0;                                    // idle 呼吸相位

/* i18n 便捷封装：统一用 storedLocale */
function vt(key: any, ...args: string[]): string { return t(key, storedLocale, ...args); }

/* ────── SVG 图标 ────── */
function svgMic(): string {
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-6 6.93V22h-2v-3.07A7 7 0 015 12v-2h2v2a5 5 0 0010 0v-2h2z"/></svg>`;
}
function svgHangup(): string {
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.68 13.34a15.46 15.46 0 004.14.84 15.46 15.46 0 004.14-.84 1 1 0 011.12.24l2.92 2.92a1 1 0 01-.12 1.42 17.8 17.8 0 01-8.06 3.08 17.8 17.8 0 01-8.06-3.08 1 1 0 01-.12-1.42l2.92-2.92a1 1 0 011.12-.24z"/><line x1="23" y1="1" x2="1" y2="23" stroke="currentColor" stroke-width="2"/></svg>`;
}
function svgCheck(): string {
  return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>`;
}
function svgPhone(): string {
  return `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>`;
}
function svgBot(): string {
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="8" width="16" height="11" rx="3"/><path d="M12 8V4"/><circle cx="12" cy="3" r="1.2" fill="currentColor"/><circle cx="9" cy="13" r="1.3" fill="currentColor" stroke="none"/><circle cx="15" cy="13" r="1.3" fill="currentColor" stroke="none"/></svg>`;
}
function svgLock(): string {
  return `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>`;
}
function svgChat(): string {
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>`;
}
function svgMicOff(): string {
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6"/><path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23"/><line x1="12" y1="19" x2="12" y2="22"/></svg>`;
}
function svgThumbUp(): string {
  return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v12"/><path d="M15 5.88L14 10h5.83a2 2 0 011.92 2.56l-2.33 8A2 2 0 0117.5 22H4a2 2 0 01-2-2v-8a2 2 0 012-2h2.76a2 2 0 001.79-1.11L12 2a3.13 3.13 0 013 3.88z"/></svg>`;
}
function svgThumbDown(): string {
  return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 14V2"/><path d="M9 18.12L10 14H4.17a2 2 0 01-1.92-2.56l2.33-8A2 2 0 016.5 2H20a2 2 0 012 2v8a2 2 0 01-2 2h-2.76a2 2 0 00-1.79 1.11L12 22a3.13 3.13 0 01-3-3.88z"/></svg>`;
}
function svgCheckCircle(): string {
  return `<svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>`;
}
function svgAlert(): string {
  return `<svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;
}

/* ────── 主渲染入口 ────── */
export function renderVoicePanel(locale: Locale, widgetId?: string): void {
  voiceState = "idle";
  viewMode = "waveform";
  storedWidgetId = widgetId || "";
  phoneNumber = "";
  phoneVerified = false;
  chatMessages = [];
  callSec = 0;
  isMuted = false;
  endRating = null;
  stopMicCapture();
  stopTimer();
  stopPvCountdown();
  setFabPulse(true);
  closeVoiceConnection();
  setShellChromeHidden(false); // 复位：确保外壳 ✕/返回可见

  const html = buildUI();
  setPanelContent(html);
  bindEvents(locale);
}

/* 通话连接态隐藏外壳关闭(✕)/返回，避免误触收起；仅以「挂断」退出。退出时恢复。 */
function setShellChromeHidden(hidden: boolean): void {
  const root = getShadowRoot();
  if (!root) return;
  showPanelBack(!hidden);
  const closeEl = root.getElementById("oc-close") as HTMLElement | null;
  if (closeEl) closeEl.style.display = hidden ? "none" : "";
}

/* ────── 关闭连接 ────── */
function closeVoiceConnection(): void {
  voiceWs?.close();
  voiceWs = null;
  audioQueue = [];
  playing = false;
  nextPlayTime = 0;
  if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }
  if (audioCtx) { try { audioCtx.close(); } catch {} audioCtx = null; }
  analyserNode = null;
  playbackAnalyser = null;
  aiThinking = false;
  orbAmp = 0;
  pcmSendBuf = [];
}

/* ────── 统一 UI 构建 ────── */
function buildUI(): string {
  return /* html */`
<div class="oc-voice" id="oc-voice-root">
  <div id="oc-voice-content"></div>
  <style>${voiceStyles()}</style>
</div>`;
}

/* ────── 更新内容区 ────── */
function updateContent(html: string): void {
  const root = getShadowRoot();
  const ct = root?.getElementById("oc-voice-content");
  if (ct) ct.innerHTML = html;
}

/* 统一手机验证组件参数（C1-C3：voice 与 callback 共用同一组件） */
function idlePvOptions(): PhoneVerifyOptions {
  return {
    locale: storedLocale,
    primaryColor: getPrimaryColor(),
    submitLabel: vt("voice.startBtn"),
    submitIcon: svgMic(),
    privacy: true,
    onVerified: () => { renderDialingFromIdle(); },
  };
}

// 校验通过 → 进入拨号
function renderDialingFromIdle(): void {
  voiceState = "dialing";
  setFabPulse(false);
  updateContent(buildDialingHTML());
  const root = getShadowRoot();
  root?.getElementById("oc-v-cancel")?.addEventListener("click", () => {
    voiceState = "idle";
    stopPvCountdown();
    setFabPulse(true);
    updateContent(buildIdleHTML());
    bindPhoneVerify(idlePvOptions());
  });
  startVoiceCall(storedLocale);
}

function buildIdleHTML(): string {
  return /* html */`
<div class="oc-v-idle">
  <div class="oc-v-header">${svgBot()} <span>${vt("voice.title")}</span></div>
  ${phoneVerifyHtml(idlePvOptions())}
</div>
`;
}

function buildDialingHTML(): string {
  return /* html */`
<div class="oc-v-dialing">
  <div class="oc-v-dialing-ring">
    <span class="oc-v-ripple"></span>
    <span class="oc-v-ripple oc-v-ripple-2"></span>
    <span class="oc-v-ripple oc-v-ripple-3"></span>
    <div class="oc-v-ring-inner">${svgPhone()}</div>
  </div>
  <p class="oc-v-dialing-text">${vt("voice.dialing")}<span class="oc-v-dots-ellipsis"><i>.</i><i>.</i><i>.</i></span></p>
  <p class="oc-v-dialing-sub">${vt("voice.dialingSub")}</p>
</div>
<button type="button" id="oc-v-cancel" class="oc-v-cancel-btn">${svgHangup()} ${vt("voice.cancel")}</button>
`;
}

function buildConnectedHTML(): string {
  const chatOn = viewMode === 'chat';
  // 整体为 flex 列：头部 + 中间内容(滚动) + 底部控制条(固定)。
  return /* html */`
<div class="oc-v-connected">
  <div class="oc-v-header-row">
    <span class="oc-v-title">${svgBot()} <span>${vt("voice.title")}</span></span>
    <label class="oc-v-toggle ${chatOn ? 'is-on' : ''}" title="${vt("voice.toggleText")}">
      <span class="oc-v-toggle-label">${svgChat()}<span>${vt("voice.toggleText")}</span></span>
      <input type="checkbox" id="oc-v-text-toggle" ${chatOn ? 'checked' : ''} aria-label="${vt("voice.toggleText")}" role="switch" aria-checked="${chatOn}">
      <span class="oc-v-toggle-track"><span class="oc-v-toggle-thumb"></span></span>
    </label>
  </div>
  <div id="oc-v-mode-content" class="${chatOn ? 'is-chat' : 'is-wave'}">
    ${chatOn ? buildChatContent() : buildWaveformContent()}
  </div>
  <div class="oc-v-bottom-bar">
    <button type="button" id="oc-v-mute" class="oc-v-ctrl-btn ${isMuted ? 'is-on' : ''}" aria-label="${isMuted ? vt("voice.unmute") : vt("voice.mute")}">
      ${isMuted ? svgMicOff() : svgMic()}<span>${isMuted ? vt("voice.muted") : vt("voice.mute")}</span>
    </button>
    <button type="button" id="oc-v-hangup" class="oc-v-hangup-btn">${svgHangup()} <span>${vt("voice.hangup")}</span></button>
  </div>
</div>
`;
}

// 声波球 DOM（waveform 大球 / chat mini 小球共用，由 mini 决定尺寸样式）。
// id 固定为 oc-v-orb，供 startWaveformAnimation 每帧写入 --amp。
function orbMarkup(mini: boolean): string {
  const dots = Array.from({ length: 12 }, (_, i) =>
    `<i class="oc-v-orb-dot" style="--i:${i}"></i>`
  ).join("");
  return /* html */`
<div class="oc-v-orb${mini ? ' oc-v-orb-mini' : ''}" id="oc-v-orb" data-state="idle">
  <div class="oc-v-orb-halo"></div>
  <div class="oc-v-orb-dots">${dots}</div>
  <div class="oc-v-orb-core"></div>
</div>`;
}

function buildWaveformContent(): string {
  return /* html */`
<div class="oc-v-orb-wrap">${orbMarkup(false)}</div>
<div class="oc-v-timer" id="oc-v-timer">${formatTime(callSec)}</div>
<p class="oc-v-status" id="oc-v-status"><span class="oc-v-status-dot"></span>${vt("voice.statusListening")}</p>
`;
}

function buildChatContent(): string {
  // 顶部 mini 声波条：保留"通话进行中/正在聆听"反馈；下方铺开双人对话。
  return /* html */`
<div class="oc-v-mini-bar">
  <div class="oc-v-mini-orb">${orbMarkup(true)}</div>
  <span class="oc-v-timer-inline" id="oc-v-timer">${formatTime(callSec)}</span>
  <span class="oc-v-status-inline" id="oc-v-status"><span class="oc-v-status-dot"></span>${vt("voice.statusListening")}</span>
</div>
<div class="oc-v-chat-list" id="oc-v-chat-list">
  ${chatMessages.map(m => buildChatBubble(m)).join("")}
  ${chatMessages.length === 0 ? `<p class="oc-v-chat-empty">${vt("voice.chatEmpty")}</p>` : ''}
</div>
`;
}

function svgUserMini(): string {
  return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
}

function buildChatBubble(m: { role: "user" | "agent"; text: string }): string {
  const isUser = m.role === "user";
  // 客户左 / AI 接待员右：头像 + 名字 + 气泡，强化双人对话观感。
  return /* html */`
<div class="oc-v-chat-row ${isUser ? 'is-user' : 'is-agent'}">
  <span class="oc-v-chat-avatar">${isUser ? svgUserMini() : svgBot()}</span>
  <div class="oc-v-chat-col">
    <span class="oc-v-chat-name">${isUser ? vt("voice.roleUser") : vt("voice.roleAgent")}</span>
    <div class="oc-v-chat-bubble">${escHtml(m.text)}</div>
  </div>
</div>`;
}

function buildEndedHTML(): string {
  const color = getPrimaryColor();
  return /* html */`
<div class="oc-v-ended-icon" style="color:#16a34a;">${svgCheckCircle()}</div>
<p class="oc-v-ended-title">${vt("voice.endedTitle")}</p>
<p class="oc-v-ended-duration">${vt("voice.duration", formatTime(callSec))}</p>
<p class="oc-v-ended-summary">${vt("voice.endedSummary")}</p>

<div class="oc-v-rate" id="oc-v-rate">
  <span class="oc-v-rate-label">${vt("voice.rateLabel")}</span>
  <div class="oc-v-rate-btns">
    <button type="button" class="oc-v-rate-btn" data-rate="up" aria-label="${vt("voice.rateUp")}">${svgThumbUp()}</button>
    <button type="button" class="oc-v-rate-btn" data-rate="down" aria-label="${vt("voice.rateDown")}">${svgThumbDown()}</button>
  </div>
</div>

<button type="button" id="oc-v-redial" class="oc-v-start-btn" style="background:${color};">
  ${svgMic()} <span>${vt("voice.redial")}</span>
</button>
<button type="button" id="oc-v-leave-contact" class="oc-v-ghost-btn" style="color:${color};border-color:${color};">${svgPhone()} <span>${vt("voice.leaveContact")}</span></button>
<button type="button" id="oc-v-close" class="oc-v-close-btn">${vt("voice.closeBtn")}</button>
`;
}

function buildErrorHTML(title: string, msg: string, showCallback: boolean): string {
  const color = getPrimaryColor();
  return /* html */`
<div class="oc-v-ended-icon" style="color:#ef4444;">${svgAlert()}</div>
<p class="oc-v-ended-title">${escHtml(title)}</p>
<p class="oc-v-ended-summary">${escHtml(msg)}</p>
<button type="button" id="oc-v-retry" class="oc-v-start-btn" style="background:${color};">${svgMic()} <span>${vt("voice.retry")}</span></button>
${showCallback ? `<button type="button" id="oc-v-err-callback" class="oc-v-ghost-btn" style="color:${color};border-color:${color};">${svgPhone()} <span>${vt("voice.switchCallback")}</span></button>` : ""}
<button type="button" id="oc-v-close" class="oc-v-close-btn">${vt("voice.closeBtn")}</button>
`;
}

/* ────── 样式 ────── */
function voiceStyles(): string {
  const c = getPrimaryColor();
  return `
.oc-v-header { font-size:16px;font-weight:600;color:var(--oc-text);text-align:center;margin-bottom:14px;padding:12px 0;display:flex;align-items:center;justify-content:center;gap:7px; }
/* 手机验证 UI 由 phone-verify 组件提供（oc-pv-*）。下方仅保留结束页/错误页复用的按钮样式。 */
.oc-v-start-btn { width:90%;margin-left:auto;margin-right:auto;padding:14px;border:none;border-radius:12px;font-size:16px;font-weight:600;color:#fff;display:flex;align-items:center;justify-content:center;gap:8px;transition:all 0.15s; }
.oc-v-start-btn:hover:not(:disabled) { opacity:0.9; }

.oc-v-dialing { display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px 0 8px; }
.oc-v-dialing-ring { display:flex;align-items:center;justify-content:center;height:150px;width:150px;position:relative;margin:8px 0 4px; }
.oc-v-ripple { position:absolute;top:50%;left:50%;width:72px;height:72px;margin:-36px 0 0 -36px;border-radius:50%;background:${c};opacity:0;animation:oc-ripple 2.4s cubic-bezier(0.2,0.6,0.3,1) infinite; }
.oc-v-ripple-2 { animation-delay:0.8s; }
.oc-v-ripple-3 { animation-delay:1.6s; }
@keyframes oc-ripple { 0%{transform:scale(0.7);opacity:0.45;} 80%{opacity:0.05;} 100%{transform:scale(2.6);opacity:0;} }
.oc-v-ring-inner { width:72px;height:72px;border-radius:50%;background:${c};color:#fff;display:flex;align-items:center;justify-content:center;z-index:1;box-shadow:0 10px 30px ${c}55;animation:oc-dial-bob 1.6s ease-in-out infinite; }
@keyframes oc-dial-bob { 0%,100%{transform:translateY(0) rotate(0);} 25%{transform:translateY(-3px) rotate(-7deg);} 75%{transform:translateY(-3px) rotate(7deg);} }
.oc-v-dialing-text { text-align:center;font-size:15px;font-weight:600;color:var(--oc-text);margin:12px 0 4px; }
.oc-v-dialing-sub { text-align:center;font-size:12px;color:var(--oc-text-secondary);margin-bottom:18px; }
.oc-v-dots-ellipsis i { animation:oc-dot-blink 1.4s infinite both;display:inline-block; }
.oc-v-dots-ellipsis i:nth-child(2){animation-delay:0.2s;} .oc-v-dots-ellipsis i:nth-child(3){animation-delay:0.4s;}
@keyframes oc-dot-blink { 0%,80%,100%{opacity:0.2;} 40%{opacity:1;} }
.oc-v-cancel-btn { width:100%;padding:12px;border:1px solid var(--oc-border);border-radius:10px;background:var(--oc-bg);color:var(--oc-text-secondary);font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px; }
.oc-v-cancel-btn svg { width:16px;height:16px; }

.oc-v-header-row { display:flex;align-items:center;justify-content:space-between;margin-bottom:8px; }
.oc-v-title { display:flex;align-items:center;gap:6px;font-size:14px;font-weight:600;color:var(--oc-text); }

/* idle 态（手机号 + 验证码）：在弹窗内高度居中展示 */
.oc-v-idle { min-height:100%;box-sizing:border-box;padding:16px;display:flex;flex-direction:column;justify-content:center; }

/* 连接态：撑满面板高度，头/尾固定，中间滚动（挂断/静音固定在底部） */
.oc-voice, #oc-voice-content { height:100%; }
.oc-v-connected { display:flex;flex-direction:column;height:100%;min-height:0;padding:12px 16px;box-sizing:border-box; }
.oc-v-connected .oc-v-header-row { flex-shrink:0; }
#oc-v-mode-content { flex:1 1 auto;min-height:0;display:flex;flex-direction:column; }
#oc-v-mode-content.is-wave { justify-content:center;overflow-y:auto; }   /* 声波球垂直居中 */
#oc-v-mode-content.is-chat { overflow:hidden; }                          /* 由内部 chat-list 滚动 */
.oc-v-mini-bar { flex-shrink:0; }
.oc-v-connected .oc-v-bottom-bar { flex-shrink:0;margin-top:12px; }
/* 文字开关（toggle switch）：标题右侧，开→双人对话 */
.oc-v-toggle { display:flex;align-items:center;gap:7px;cursor:pointer;user-select:none; }
.oc-v-toggle-label { display:flex;align-items:center;gap:4px;font-size:12px;font-weight:500;color:var(--oc-text-secondary);transition:color 0.18s; }
.oc-v-toggle-label svg { width:14px;height:14px; }
.oc-v-toggle input { position:absolute;width:1px;height:1px;opacity:0;pointer-events:none; }
.oc-v-toggle-track { position:relative;width:38px;height:22px;border-radius:11px;background:var(--oc-border);transition:background 0.18s;flex-shrink:0; }
.oc-v-toggle-thumb { position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,0.25);transition:transform 0.18s; }
.oc-v-toggle.is-on .oc-v-toggle-track { background:${c}; }
.oc-v-toggle.is-on .oc-v-toggle-thumb { transform:translateX(16px); }
.oc-v-toggle.is-on .oc-v-toggle-label { color:${c}; }

/* chat 模式顶部 mini 声波条（保留通话进行/正在聆听反馈） */
.oc-v-mini-bar { display:flex;align-items:center;gap:10px;padding:2px 4px 10px;border-bottom:1px solid var(--oc-border);margin-bottom:10px; }
.oc-v-mini-orb { position:relative;width:40px;height:40px;flex-shrink:0; }
.oc-v-orb.oc-v-orb-mini { position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) scale(0.25);transform-origin:center; }
.oc-v-timer-inline { font-size:16px;font-weight:500;color:var(--oc-text);font-variant-numeric:tabular-nums; }
.oc-v-status-inline { font-size:12px;color:var(--oc-text-secondary);display:flex;align-items:center;gap:5px;margin-left:auto; }

/* ───── OpenAI 风格声波球（白圈 + 围绕小圆点，随声音波动） ───── */
.oc-v-orb-wrap { display:flex;justify-content:center;align-items:center;height:180px;margin-bottom:4px; }
.oc-v-orb {
  position:relative;width:160px;height:160px;display:flex;align-items:center;justify-content:center;
  --amp:0;                       /* 0..1 振幅，由 JS 每帧写入 */
  --orb:${c};                    /* 主题色，状态切换时覆盖 */
}
/* 中心白圈：随振幅缩放 + 辉光增强 */
.oc-v-orb-core {
  position:relative;z-index:2;width:84px;height:84px;border-radius:50%;
  background:radial-gradient(circle at 35% 30%, #ffffff 0%, #f2f4ff 55%, #e3e8ff 100%);
  box-shadow:0 0 0 1px rgba(255,255,255,0.7),
             0 6px 22px rgba(0,0,0,0.10),
             0 0 calc(16px + var(--amp)*70px) color-mix(in srgb, var(--orb) 70%, transparent);
  transform:scale(calc(1 + var(--amp)*0.42));
  transition:transform 0.07s ease-out, box-shadow 0.1s ease-out;
}
/* 辉光：白圈背后的彩色光晕 */
.oc-v-orb-halo {
  position:absolute;z-index:1;width:84px;height:84px;border-radius:50%;
  background:var(--orb);filter:blur(20px);
  opacity:calc(0.18 + var(--amp)*0.55);
  transform:scale(calc(1.15 + var(--amp)*0.9));
  transition:transform 0.09s ease-out, opacity 0.12s ease-out;
}
/* 围绕小圆点：随振幅向外推、变亮 */
.oc-v-orb-dots { position:absolute;inset:0;z-index:1; }
.oc-v-orb-dot {
  position:absolute;top:50%;left:50%;width:6px;height:6px;border-radius:50%;background:var(--orb);
  transform:translate(-50%,-50%) rotate(calc(var(--i)*30deg)) translateY(calc(-52px - var(--amp)*40px));
  opacity:calc(0.22 + var(--amp)*0.78);
  transition:transform 0.09s ease-out, opacity 0.12s ease-out;
}
/* 状态配色 */
.oc-v-orb[data-state="listening"] { --orb:#22c55e; }
.oc-v-orb[data-state="speaking"]  { --orb:${c}; }
.oc-v-orb[data-state="thinking"]  { --orb:#f59e0b; }
.oc-v-orb[data-state="idle"]      { --orb:${c}; }

.oc-v-timer { text-align:center;font-size:28px;font-weight:300;color:var(--oc-text);font-variant-numeric:tabular-nums;margin-bottom:4px; }
.oc-v-status { font-size:13px;color:var(--oc-text-secondary);text-align:center;margin-bottom:12px;display:flex;align-items:center;justify-content:center;gap:6px; }
.oc-v-status-dot { width:8px;height:8px;border-radius:50%;background:#22c55e;animation:oc-status-blink 1.6s ease-in-out infinite; }
@keyframes oc-status-blink { 0%,100%{opacity:1;} 50%{opacity:0.35;} }

/* 双人对话：客户左 / AI 接待员右，各带头像 + 名字 */
.oc-v-chat-list { flex:1 1 auto;min-height:0;overflow-y:auto;padding:0 4px; }
.oc-v-chat-row { display:flex;align-items:flex-start;gap:8px;margin-bottom:12px; }
.oc-v-chat-row.is-user { justify-content:flex-start; }
.oc-v-chat-row.is-agent { flex-direction:row-reverse; }
.oc-v-chat-avatar { flex-shrink:0;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:var(--oc-bg);border:1px solid var(--oc-border);color:var(--oc-text-secondary); }
.oc-v-chat-avatar svg { width:16px;height:16px; }
.oc-v-chat-row.is-agent .oc-v-chat-avatar { background:${c};border-color:${c};color:#fff; }
.oc-v-chat-col { display:flex;flex-direction:column;gap:3px;max-width:78%; }
.oc-v-chat-row.is-agent .oc-v-chat-col { align-items:flex-end; }
.oc-v-chat-name { font-size:11px;color:var(--oc-text-secondary);padding:0 2px; }
.oc-v-chat-bubble { padding:10px 14px;border-radius:14px;font-size:13px;line-height:1.5;word-break:break-word; }
.oc-v-chat-row.is-user .oc-v-chat-bubble { background:var(--oc-bg);border:1px solid var(--oc-border);color:var(--oc-text);border-top-left-radius:4px; }
.oc-v-chat-row.is-agent .oc-v-chat-bubble { background:${c};color:#fff;border-top-right-radius:4px; }
.oc-v-chat-empty { text-align:center;font-size:13px;color:var(--oc-text-secondary);padding:30px 0; }

.oc-v-bottom-bar { display:flex;justify-content:center;align-items:center;gap:12px;margin-top:16px; }
.oc-v-ctrl-btn { padding:11px 18px;border:1px solid var(--oc-border);border-radius:25px;background:var(--oc-bg);color:var(--oc-text-secondary);font-size:14px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all 0.15s; }
.oc-v-ctrl-btn:hover { border-color:${c};color:${c}; }
.oc-v-ctrl-btn.is-on { background:#fef2f2;border-color:#ef4444;color:#ef4444; }
.oc-v-ctrl-btn svg { width:18px;height:18px; }
.oc-v-hangup-btn { padding:11px 28px;border:none;border-radius:25px;background:#ef4444;color:#fff;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all 0.15s; }
.oc-v-hangup-btn:hover { background:#dc2626; }
.oc-v-hangup-btn svg { width:18px;height:18px; }

.oc-v-ended-icon { display:flex;justify-content:center;margin-bottom:12px; }
.oc-v-ended-title { text-align:center;font-size:18px;font-weight:600;color:var(--oc-text);margin-bottom:8px; }
.oc-v-ended-duration { text-align:center;font-size:14px;color:var(--oc-text-secondary);margin-bottom:8px; }
.oc-v-ended-summary { text-align:center;font-size:13px;color:var(--oc-text-secondary);line-height:1.6;margin:0 0 18px;padding:0 8px; }
.oc-v-rate { display:flex;flex-direction:column;align-items:center;gap:10px;margin-bottom:18px; }
.oc-v-rate-label { font-size:13px;color:var(--oc-text); }
.oc-v-rate-btns { display:flex;gap:16px; }
.oc-v-rate-btn { width:48px;height:48px;border:1px solid var(--oc-border);border-radius:12px;background:var(--oc-bg);color:var(--oc-text-secondary);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all 0.15s; }
.oc-v-rate-btn:hover { border-color:${c};color:${c};transform:translateY(-2px); }
.oc-v-rate-thanks { font-size:13px;color:#15803d;font-weight:500; }
.oc-v-ghost-btn { width:90%;margin-left:auto;margin-right:auto;padding:14px;border:1px solid ${c};border-radius:12px;background:transparent;font-size:16px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;margin-top:10px; }
.oc-v-ghost-btn svg { width:18px;height:18px; }
.oc-v-close-btn { width:90%;margin-left:auto;margin-right:auto;padding:14px;border:1px solid var(--oc-border);border-radius:12px;background:transparent;color:var(--oc-text-secondary);font-size:16px;cursor:pointer;margin-top:10px;display:flex;align-items:center;justify-content:center; }
`;
}

/* ────── 工具函数 ────── */
function escHtml(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}
function formatTime(sec: number): string {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}
function getPrimaryColor(): string {
  const root = getShadowRoot();
  if (root) {
    const c = getComputedStyle(root.host).getPropertyValue("--oc-primary").trim();
    if (c) return c;
  }
  return "#533afd";
}

/* ────── 事件绑定（手机验证统一走 phone-verify 组件 C1-C3） ────── */
function bindEvents(locale: Locale): void {
  storedLocale = locale;
  const root = getShadowRoot();
  if (!root) return;
  voiceState = "idle";
  chatMessages = [];
  callSec = 0;
  stopTimer();
  stopPvCountdown();
  setFabPulse(true);
  updateContent(buildIdleHTML());
  bindPhoneVerify(idlePvOptions());
}

/* ────── ChatGPT 风格动画 ────── */
// WS 事件只更新语义状态；真正的视觉由 orb 动画循环按音频电平驱动
function setAnimMode(mode: "idle" | "thinking" | "responding" | "volume"): void {
  animMode = mode;
  aiThinking = mode === "thinking";
}

// 从分析器读取归一化电平 0..1
function readLevel(node: AnalyserNode | null): number {
  if (!node) return 0;
  const data = new Uint8Array(node.frequencyBinCount);
  node.getByteFrequencyData(data);
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i];
  const avg = sum / data.length;        // 0..255
  return Math.min(1, avg / 90);         // ~中等音量映射到接近满幅
}

function updateOrbStatus(state: string): void {
  const root = getShadowRoot();
  const el = root?.getElementById("oc-v-status");
  if (!el) return;
  const label =
    state === "speaking" ? vt("voice.statusSpeaking") :
    state === "thinking" ? vt("voice.statusThinking") :
    state === "listening" ? vt("voice.statusListeningShort") :
    vt("voice.statusListening");
  el.innerHTML = `<span class="oc-v-status-dot"></span>${label}`;
}

// OpenAI 风格声波球：白圈 + 围绕小圆点随声音波动
function startWaveformAnimation(): void {
  orbAmp = 0;
  orbPhase = 0;

  function loop(): void {
    if (voiceState !== "connected") return;

    const mic = readLevel(analyserNode);
    const ai = readLevel(playbackAnalyser);
    orbPhase += 0.045;
    const breathe = Math.sin(orbPhase) * 0.5 + 0.5;   // 0..1 缓慢呼吸

    let target: number;
    let state: string;
    if (playing && ai > 0.05) {
      state = "speaking";  target = Math.max(ai, 0.14);
    } else if (mic > 0.08) {
      state = "listening"; target = mic;
    } else if (aiThinking) {
      state = "thinking";  target = 0.12 + breathe * 0.12;
    } else {
      state = "idle";      target = 0.04 + breathe * 0.08;
    }

    // 平滑过渡，避免抖动
    orbAmp += (target - orbAmp) * 0.3;

    const root = getShadowRoot();
    const orb = root?.getElementById("oc-v-orb") as HTMLElement | null;
    if (orb) {
      orb.style.setProperty("--amp", orbAmp.toFixed(3));
      if (orb.dataset.state !== state) {
        orb.dataset.state = state;
        updateOrbStatus(state);
      }
    }
    animFrameId = requestAnimationFrame(loop);
  }

  animFrameId = requestAnimationFrame(loop);
}

function stopWaveformAnimation(): void {
  if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }
}

/* ────── 计时器 ────── */
function startTimer(): void {
  stopTimer();
  callSec = 0;
  const root = getShadowRoot();
  timerInterval = setInterval(() => {
    callSec++;
    const el = root?.getElementById("oc-v-timer");
    if (el) el.textContent = formatTime(callSec);
  }, 1000);
}

function stopTimer(): void {
  if (timerInterval) { clearInterval(timerInterval); timerInterval = null; }
  callSec = 0;
}

/* ────── WebSocket URL ────── */
function getVoiceWsUrl(): string {
  const overridden = (window as any).__ONECALL_VOICE_WS__;
  if (overridden) return overridden;
  // 根据 API base 决定 WS 地址：API 在本地则直连，否则走生产
  // 不再依赖 window.location.hostname，避免 localhost:4321 代理到远程 API 时误判
  const apiBase = apiBaseUrl();
  const isLocalApi = apiBase.includes("localhost") || apiBase.includes("127.0.0.1");
  const url = isLocalApi ? "ws://127.0.0.1:8765" : "wss://16x.ai/ws";
  console.log("[voice] getVoiceWsUrl =>", url, "apiBase:", apiBase, "overridden:", overridden);
  return url;
}

function bindConnectedEventsGlobal(): void {
  const root = getShadowRoot();
  if (!root) return;
  // 文字开关：开→双人对话（顶部 mini 声波条），关→大声波球。同弹窗内切换，不挂断。
  root.getElementById("oc-v-text-toggle")?.addEventListener("change", (e) => {
    viewMode = (e.target as HTMLInputElement).checked ? "chat" : "waveform";
    stopWaveformAnimation();
    updateContent(buildConnectedHTML());
    bindConnectedEventsGlobal();
    startWaveformAnimation(); // 两态都有 #oc-v-orb（chat 为 mini），统一驱动动画
    if (viewMode === "chat") refreshChatView(); // 滚动到底，呈现已有对话
  });
  root.getElementById("oc-v-mute")?.addEventListener("click", () => toggleMute());
  root.getElementById("oc-v-hangup")?.addEventListener("click", () => {
    manualHangup = true;
    endVoiceCall();
    showEndedUI();
  });
}

function toggleMute(): void {
  isMuted = !isMuted;
  if (micStream) micStream.getAudioTracks().forEach((tr) => { tr.enabled = !isMuted; });
  const root = getShadowRoot();
  const btn = root?.getElementById("oc-v-mute");
  if (btn) {
    btn.classList.toggle("is-on", isMuted);
    btn.innerHTML = (isMuted ? svgMicOff() : svgMic()) + "<span>" + (isMuted ? vt("voice.muted") : vt("voice.mute")) + "</span>";
    btn.setAttribute("aria-label", isMuted ? vt("voice.unmute") : vt("voice.mute"));
  }
}

// 结束页：满意度快评 + 留资 CTA（G1）
function bindEndedExtras(root: ShadowRoot | null): void {
  if (!root) return;
  endRating = null;
  root.querySelectorAll(".oc-v-rate-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      endRating = (btn as HTMLElement).getAttribute("data-rate") as "up" | "down";
      const rateEl = root.getElementById("oc-v-rate");
      if (rateEl) rateEl.innerHTML = `<span class="oc-v-rate-thanks">${vt("voice.rateThanks")}</span>`;
    });
  });
  root.getElementById("oc-v-leave-contact")?.addEventListener("click", () => {
    renderCallbackPanel(storedLocale, storedWidgetId);
  });
}

function showEndedUI(): void {
  voiceState = "ended";
  stopTimer();
  stopWaveformAnimation();
  setFabPulse(true);
  updateContent(buildEndedHTML());
  const root = getShadowRoot();
  bindEndedExtras(root);
  root?.getElementById("oc-v-redial")?.addEventListener("click", () => {
    voiceState = "dialing";
    setFabPulse(false);
    updateContent(buildDialingHTML());
    root?.getElementById("oc-v-cancel")?.addEventListener("click", () => {
      voiceState = "idle";
      stopPvCountdown();
      setFabPulse(true);
      updateContent(buildIdleHTML());
      bindIdleEventsStatic();
    });
    startVoiceCall(storedLocale);
  });
  root?.getElementById("oc-v-close")?.addEventListener("click", () => {
    closePanel();
  });
}

// 重新进入手机验证（结束/取消后回到 idle 时复用统一组件）
function bindIdleEventsStatic(): void {
  bindPhoneVerify(idlePvOptions());
}

/* ────── 开始语音通话 ────── */
async function startVoiceCall(locale: Locale): Promise<void> {
  const root = getShadowRoot();
  if (!root) return;

  try {
    micStream = await navigator.mediaDevices.getUserMedia({
      audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });

    audioCtx = new AudioContext({ sampleRate: 16000 });
    analyserNode = audioCtx.createAnalyser();
    analyserNode.fftSize = 128;

    const wsUrl = getVoiceWsUrl();
    voiceWs = new WebSocket(wsUrl);
    voiceWs.binaryType = "arraybuffer";
    console.log("[voice] WS connecting to:", wsUrl);

    await new Promise<void>((resolve, reject) => {
      voiceWs!.onopen = () => {
        console.log("[voice] WS open");
        voiceWs!.send(JSON.stringify({
          provider: "widget",
          params: {
            agent_id: storedWidgetId,
            from: "zh",
            to: "zh",
            inputSampleRate: 16000,
            modalities: ["text", "audio"],
          },
        }));
      };
      voiceWs!.onmessage = (event) => {
        if (typeof event.data === "string") {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === "connected" || msg.type === "conversation_started" || msg.type === "greeting" || msg.type === "interrupt") {
              if (msg.type !== "interrupt") {
                console.log("[voice] 握手成功:", msg.type);
              }
              resolve();
            } else if (msg.type === "transcription") {
              const data = msg.data;
              console.log("[voice] transcription:", JSON.stringify(data));
              if (data?.text) {
                const isFinal = data.isFinal;
                // 服务端发的 AI 角色是 "assistant"（也兼容 "agent"）→ 归为 agent（右侧气泡）；
                // 否则为 user（左侧）。此前漏判 "assistant" 导致全部被当成 user 挤在一处。
                const role: "user" | "agent" = (data.role === "agent" || data.role === "assistant") ? "agent" : "user";
                // 动画状态: agent说话→responding, user说话→thinking
                if (role === "agent") {
                  if (isFinal) setAnimMode("idle");
                  else setAnimMode("responding");
                } else {
                  setAnimMode("thinking");
                }
                // 存储转录:final 追加新消息,partial 更新或追加
                if (isFinal) {
                  chatMessages.push({ role, text: data.text });
                } else {
                  const last = chatMessages[chatMessages.length - 1];
                  if (last && last.role === role) {
                    last.text = data.text; // 更新同角色的最后一条消息
                  } else {
                    chatMessages.push({ role, text: data.text });
                  }
                }
                // 更新聊天视图(无论哪个 tab,始终保持数据更新)
                if (viewMode === "chat" && voiceState === "connected") {
                  refreshChatView();
                }
              }
            } else if (msg.type === "interrupt") {
              // 打断信号：用户开口/新一轮 TTS，丢弃正在播放与排队的旧音频，避免多声叠加
              flushAudio();
              setAnimMode("thinking");
            } else if (msg.type === "error") {
              reject(new Error(msg.data?.message || msg.message || vt("voice.errUnknown")));
            } else {
              console.log("[voice] 未识别的消息类型:", msg.type, msg);
            }
          } catch (e) {
            console.error("[voice] 消息解析失败:", e, typeof event.data === "string" ? event.data.slice(0, 200) : "(binary)");
          }
        } else if (event.data instanceof ArrayBuffer) {
          playPcm(event.data);
        }
      };
      voiceWs!.onerror = (e) => { console.error("[voice] WS error event", e); reject(new Error("WebSocket 连接失败")); };
      voiceWs!.onclose = (e) => {
        console.log("[voice] WS onclose: code=", e?.code, "reason=", e?.reason, "state=", voiceState, "wasClean=", e?.wasClean);
        if (manualHangup) { manualHangup = false; return; }
        if (voiceState === "connected") {
          endVoiceCall();
          showEndedUI();
        } else if (voiceState === "dialing") {
          // 连接过程中断开,回到 idle
          console.log("[voice] dialing interrupted, returning to idle");
          stopMicCapture();
          closeVoiceConnection();
          voiceState = "idle";
          setFabPulse(true);
          updateContent(buildIdleHTML());
          bindIdleEventsStatic();
        }
      };
    });

    // 连接成功
    voiceState = "connected";
    setShellChromeHidden(true); // 通话中去掉外壳 ✕/返回
    updateContent(buildConnectedHTML());
    bindConnectedEventsGlobal();
    startPcmCapture();
    startWaveformAnimation();
    startTimer();

  } catch (err: any) {
    console.error("[voice] 连接失败:", err?.name, err?.message);
    voiceState = "error";
    stopMicCapture();
    closeVoiceConnection();
    setFabPulse(true);

    const name = err?.name || "";
    const micDenied = name === "NotAllowedError" || name === "SecurityError" || /permission|denied/i.test(err?.message || "");
    const noMic = name === "NotFoundError" || name === "DevicesNotFoundError";
    let title = vt("voice.errFailTitle");
    let msg = err?.message || vt("voice.errUnknown");
    if (micDenied) {
      title = vt("voice.errMicDeniedTitle");
      msg = vt("voice.errMicDeniedMsg");
    } else if (noMic) {
      title = vt("voice.errNoMicTitle");
      msg = vt("voice.errNoMicMsg");
    } else {
      msg = vt("voice.errFailMsg") + (err?.message ? "（" + err.message + "）" : "");
    }

    updateContent(buildErrorHTML(title, msg, true));
    const root = getShadowRoot();
    root?.getElementById("oc-v-retry")?.addEventListener("click", () => { voiceState = "idle"; renderIdle(); });
    root?.getElementById("oc-v-err-callback")?.addEventListener("click", () => { renderCallbackPanel(storedLocale, storedWidgetId); });
    root?.getElementById("oc-v-close")?.addEventListener("click", () => {
      closePanel();
    });
  }
}

function showCallEnded(): void {
  voiceState = "ended";
  stopTimer();
  stopWaveformAnimation();
  setFabPulse(true);
  closeVoiceConnection();
  updateContent(buildEndedHTML());

  const root = getShadowRoot();
  bindEndedExtras(root);
  root?.getElementById("oc-v-redial")?.addEventListener("click", () => {
    // 重新对话:直接 dialing,跳过验证
    voiceState = "dialing";
    setFabPulse(false);
    updateContent(buildDialingHTML());
    root?.getElementById("oc-v-cancel")?.addEventListener("click", () => {
      voiceState = "idle";
      stopPvCountdown();
      setFabPulse(true);
      updateContent(buildIdleHTML());
      bindIdleEventsStatic();
    });
    startVoiceCall(storedLocale);
  });
  root?.getElementById("oc-v-close")?.addEventListener("click", () => {
    closePanel();
  });
}

function refreshChatView(): void {
  const root = getShadowRoot();
  const list = root?.getElementById("oc-v-chat-list");
  if (!list) return;
  list.innerHTML = chatMessages.length === 0
    ? `<p class="oc-v-chat-empty">${vt("voice.chatEmpty")}</p>`
    : chatMessages.map(m => buildChatBubble(m)).join("");
  list.scrollTop = list.scrollHeight;
}

/* ────── PCM 音频采集与发送 ────── */
let workletNode: AudioWorkletNode | null = null;

function startPcmCapture(): void {
  if (!audioCtx || !micStream) return;

  const source = audioCtx.createMediaStreamSource(micStream);
  analyserNode = audioCtx.createAnalyser();
  analyserNode.fftSize = 128;
  analyserNode.smoothingTimeConstant = 0.6;
  source.connect(analyserNode);

  // AI TTS 播放电平分析器（playNext 中把每个 BufferSource 连到这里）
  playbackAnalyser = audioCtx.createAnalyser();
  playbackAnalyser.fftSize = 128;
  playbackAnalyser.smoothingTimeConstant = 0.6;

  // AudioWorklet: 独立线程，不干扰 WebSocket。
  // ⚠️ process() 每次仅 128 采样（≈256B），必须在 worklet 内累积到一整帧
  //    （FRAME_SAMPLES=1600 采样 = 3200B = 100ms@16k）再 postMessage，
  //    否则下游「单包≥3200B 才发送」的逻辑永远不满足，麦克风音频一帧都发不出去。
  const workletCode = `
    const FRAME_SAMPLES = 1600; // 100ms @ 16kHz
    class PcmCaptureProcessor extends AudioWorkletProcessor {
      constructor() { super(); this._buf = new Int16Array(FRAME_SAMPLES); this._n = 0; }
      process(inputs) {
        const input = inputs[0];
        if (!input || !input[0] || !input[0].length) return true;
        const channel = input[0];
        const inputRate = sampleRate;
        const targetRate = 16000;
        let floatSamples;
        if (Math.abs(inputRate - targetRate) < 100) {
          floatSamples = channel;
        } else {
          const ratio = targetRate / inputRate;
          const outLen = Math.floor(channel.length * ratio);
          floatSamples = new Float32Array(outLen);
          for (let i = 0; i < outLen; i++) {
            const srcIdx = i / ratio, srcFloor = Math.floor(srcIdx), frac = srcIdx - srcFloor;
            const a = channel[Math.min(srcFloor, channel.length - 1)];
            const b = channel[Math.min(srcFloor + 1, channel.length - 1)];
            floatSamples[i] = a + (b - a) * frac;
          }
        }
        // 累积重采样后的 16k 采样，凑满一帧即发送
        for (let i = 0; i < floatSamples.length; i++) {
          let s = floatSamples[i];
          s = s < -1 ? -1 : (s > 1 ? 1 : s);
          this._buf[this._n++] = Math.round(s * 32767);
          if (this._n === FRAME_SAMPLES) {
            const out = new Int16Array(this._buf); // 拷贝出整帧（this._buf 留作复用）
            this.port.postMessage(out.buffer, [out.buffer]);
            this._n = 0;
          }
        }
        return true;
      }
    }
    registerProcessor("pcm-capture-processor", PcmCaptureProcessor);
  `;
  const blob = new Blob([workletCode], { type: "application/javascript" });
  const workletUrl = URL.createObjectURL(blob);

  audioCtx.audioWorklet.addModule(workletUrl).then(() => {
    URL.revokeObjectURL(workletUrl);
    if (voiceState !== "connected" || !audioCtx) return;

    workletNode = new AudioWorkletNode(audioCtx, "pcm-capture-processor");
    workletNode.port.onmessage = (event) => {
      if (voiceState !== "connected" || !voiceWs || voiceWs.readyState !== WebSocket.OPEN) return;
      const pcmBuffer: ArrayBuffer = event.data; // 每包恰好一整帧（3200B）

      // 将 PCM 分帧发送（worklet 已凑满 PCM_FRAME_BYTES，此处通常发一帧）
      const bytes = new Uint8Array(pcmBuffer);
      let offset = 0;
      while (offset + PCM_FRAME_BYTES <= bytes.length) {
        const frame = bytes.slice(offset, offset + PCM_FRAME_BYTES);
        try {
          if (voiceWs && voiceWs.readyState === WebSocket.OPEN) {
            voiceWs.send(frame.buffer.slice(frame.byteOffset, frame.byteOffset + frame.byteLength));
          }
        } catch (e) {
          console.warn("[voice] PCM send failed:", e);
        }
        offset += PCM_FRAME_BYTES;
      }
    };

    source.connect(workletNode);
    // 必须把 worklet 接入图（经 0 增益→destination），否则部分浏览器不驱动其 process()。
    // 输出本身为静音（process 不写 outputs），不会产生回放声音。
    try {
      const sink = audioCtx.createGain();
      sink.gain.value = 0;
      workletNode.connect(sink);
      sink.connect(audioCtx.destination);
    } catch (e) {
      console.warn("[voice] worklet sink connect failed (process 仍可能运行):", e);
    }
    console.log("[voice] AudioWorklet PCM capture started");
  }).catch((e) => {
    console.error("[voice] AudioWorklet init failed:", e);
  });
}

function stopMicCapture(): void {
  if (workletNode) { workletNode.disconnect(); workletNode = null; }
  if (scriptNode) { scriptNode.disconnect(); scriptNode = null; }
  if (micStream) { micStream.getTracks().forEach(t => t.stop()); micStream = null; }
  pcmSendBuf = [];
}

/* ────── PCM 音频播放 ────── */
async function playPcm(arrayBuf: ArrayBuffer): Promise<void> {
  if (!audioCtx) return;
  if (audioCtx.state === "suspended") await audioCtx.resume();

  const bytes = new Uint8Array(arrayBuf);
  let offset = 0;
  if (bytes.length > 44 && bytes[0] === 0x52 && bytes[1] === 0x49) offset = 44;

  const pcmLen = (bytes.length - offset) / 2;
  if (pcmLen <= 0) return;
  const pcm16 = new Int16Array(pcmLen);
  const view = new DataView(arrayBuf, offset);
  for (let i = 0; i < pcmLen; i++) pcm16[i] = view.getInt16(i * 2, true);

  const f32 = new Float32Array(pcmLen);
  for (let i = 0; i < pcmLen; i++) f32[i] = pcm16[i] / 32768;

  audioQueue.push(f32);
  if (!playing) playNext();
}

function playNext(): void {
  if (audioQueue.length === 0) { playing = false; currentSource = null; return; }
  playing = true;
  try {
    const samples = audioQueue.shift()!;
    const buffer = audioCtx!.createBuffer(1, samples.length, 16000);
    buffer.getChannelData(0).set(samples);
    const source = audioCtx!.createBufferSource();
    source.buffer = buffer;
    source.connect(audioCtx!.destination);
    if (playbackAnalyser) source.connect(playbackAnalyser);  // 供声波球读取 AI 说话电平
    const now = audioCtx!.currentTime;
    const startTime = Math.max(now, nextPlayTime);
    source.start(startTime);
    nextPlayTime = startTime + buffer.duration;
    currentSource = source;
    source.onended = () => { if (currentSource === source) currentSource = null; playNext(); };
  } catch (e) {
    console.error("[voice] playNext error:", e);
    playing = false;
    playNext(); // 出错后继续尝试播放下一个
  }
}

/**
 * 打断当前播放（barge-in）：清空待播队列 + 停止正在播放的音频源 + 重置时间线。
 * 服务端在用户开口或新一轮 TTS 开始时会发送 {type:"interrupt"}，
 * 客户端必须立即丢弃陈旧音频，否则旧开场白/旧应答会与新音频叠加，听感像多个声音同时说话。
 */
function flushAudio(): void {
  audioQueue = [];
  if (currentSource) {
    try { currentSource.onended = null; currentSource.stop(); } catch { /* 可能已结束 */ }
    currentSource = null;
  }
  playing = false;
  nextPlayTime = 0;
}

/* ────── 结束通话 ────── */
function endVoiceCall(): void {
  if (voiceState !== "connected") return;
  voiceState = "ended";
  setShellChromeHidden(false); // 恢复外壳 ✕/返回
  stopMicCapture();
  voiceWs?.close();
  voiceWs = null;
  audioQueue = [];
  playing = false;
  nextPlayTime = 0;
  if (audioCtx && audioCtx.state !== "closed") {
    try { audioCtx.close(); } catch {}
    audioCtx = null;
  }
  analyserNode = null;
}
